/**
 * Ordering for "Group by Homeroom" batches.
 *
 * Shared because two surfaces print homeroom-grouped stacks — Access Sheets
 * (`/connect/access-sheets`) and Printables (`/reports/printables`) — and a
 * stack that groups differently depending on which page produced it is worse
 * than one that never grouped at all.
 *
 * ## Why this exists
 *
 * Access Sheets shipped a "Group by Homeroom?" toggle whose only effect was to
 * add a homeroom segment to each sheet's filing slug. Nothing was reordered:
 * the batch came out in selection order. The control's own copy promises to
 * "save time handing out sheets by pre-grouping them by homeroom", which a
 * label alone does not do — you still had to sort the pile by hand. This module
 * is the missing half.
 *
 * ## The order
 *
 * Homeroom A→Z, then students within each homeroom by the caller's own sort
 * name, so the existing first/last-name preference still decides the order
 * inside a homeroom rather than being overridden.
 *
 * Students with no homeroom sort LAST, as one block, never interleaved
 * alphabetically among the named ones. They are the pile someone has to deal
 * with separately, and burying "No Homeroom" between "Nelson" and "Ortiz" is
 * exactly the manual re-sorting the toggle is meant to remove. This matches the
 * explicit `NO_HOMEROOM_LABEL` the printed slug already falls back to.
 */

/** Sorts blank/unknown homerooms after every named one, then A→Z. */
function compareHomeroomNames(a: string, b: string): number {
  const left = a.trim();
  const right = b.trim();
  if (!left && !right) return 0;
  // Unassigned students form their own trailing block.
  if (!left) return 1;
  if (!right) return -1;
  return left.localeCompare(right);
}

/**
 * Order a batch for printing, grouped by homeroom.
 *
 * Stable within a homeroom via `sortName`, and non-mutating — callers pass the
 * array they are about to hand to a PDF generator, which should not be
 * reordered underneath them.
 *
 * @param items      - The batch, in whatever order the caller assembled it.
 * @param getHomeroom - Homeroom name for an item; `''`/undefined = unassigned.
 * @param getSortName - Within-homeroom sort key (the caller's first/last-name
 *                      preference, already resolved).
 */
export function orderByHomeroom<T>(
  items: readonly T[],
  getHomeroom: (item: T) => string | undefined | null,
  getSortName: (item: T) => string,
): T[] {
  return [...items].sort((a, b) => {
    const byHomeroom = compareHomeroomNames(getHomeroom(a) ?? '', getHomeroom(b) ?? '');
    if (byHomeroom !== 0) return byHomeroom;
    return getSortName(a).localeCompare(getSortName(b));
  });
}
